import { TCommonVipBadgeTypeV2 } from '~/api/converters/common/V2/VipDiscountBadge/index.type'
import {
  CommonVipBadgeType,
  TCommonVipBadgeType,
} from '~/api/converters/global/common/VipDiscountBadge/index.type'
import {
  AriProxyApiRatePlanPriceType,
  TAriProxyApiRatePlanPriceType,
} from '~/api/services/ari-proxy-api/index.type'
import { toKRW } from '~/api/utils/krNumberToString'

export const MemberClassMap = {
  LEVEL_00: 0, // 일반
  LEVEL_01: 1, // 골드
  LEVEL_02: 2, // 핑크
  LEVEL_03: 3, // 로얄 핑크
}
export type MemberClass = keyof typeof MemberClassMap

/**
 * @summary 해외주문 VIP 요금 여부 확인 > 가격 타입 확인
 * @description B2B 가격 타입인 경우 골드 클래스 타입.
 * {@link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29233997/B2B+API}
 * @param priceType
 */
export const getGlobalVipBadgeType = (priceType: TAriProxyApiRatePlanPriceType) => {
  switch (priceType) {
    case AriProxyApiRatePlanPriceType.B2B:
      return CommonVipBadgeType.GOLD
    case AriProxyApiRatePlanPriceType.FLIGHT:
      return CommonVipBadgeType.FLIGHT
    default:
      return undefined
  }
}

/**
 * @summary 해외주문 VIP 요금 여부 확인 > 할인 적용 금액 노출 조건
 * @description 할인 적용 금액이 1000원 이상인 경우에만 안내 영역 노출
 * {@link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29233997/B2B+API}
 * @param discountAmount 할인되는 금액
 */
export const getIsGlobalVipDiscountAmount = (discountAmount: number) => {
  return discountAmount >= 1000
}

/**
 * @summary VIP 요금 여부 확인 > 할인 금액 안내
 * @description VIP 할인 금액이 1000원 이상인 경우에만 금액 반환
 * @param discountAmount 할인되는 금액
 */
export const getVipDiscountNoticeAmountText = ({
  priceType,
  discountAmount,
}: {
  priceType: TAriProxyApiRatePlanPriceType | undefined
  discountAmount: number | undefined
}): string => {
  if (!priceType || !discountAmount) {
    return ''
  }

  // 가격 타입이 Vip 요금제인지 (Gold 등급)
  const isVip = getGlobalVipBadgeType(priceType) === CommonVipBadgeType.GOLD
  // 할인 금액이 1000원 이상인지
  const isVipDiscount = getIsGlobalVipDiscountAmount(discountAmount)

  return !!isVip && isVipDiscount ? toKRW(discountAmount) : ''
}

/**
 * for V2
 */
export const getGlobalVipDiscountAmount = ({
  priceType,
  discountAmount,
}: {
  priceType: TAriProxyApiRatePlanPriceType | undefined
  discountAmount: number | undefined
}): number => {
  if (!priceType || !discountAmount) {
    return 0
  }

  const isVip = getGlobalVipBadgeType(priceType) === CommonVipBadgeType.GOLD
  const isVipDiscount = getIsGlobalVipDiscountAmount(discountAmount)

  return !!isVip && isVipDiscount ? discountAmount : 0
}

export const getVipMemberClass = (memberClass?: string): MemberClass | undefined => {
  return memberClass && MemberClassMap[memberClass] > 0 ? (memberClass as MemberClass) : undefined
}

export const getDomesticVipBadgeType = (data?: string): TCommonVipBadgeType | '' => {
  const memberClass = getVipMemberClass(data)
  switch (memberClass) {
    case 'LEVEL_01':
      return CommonVipBadgeType.GOLD
    default:
      return ''
  }
}

/**
 * @summary 해외주문 VIP 요금 여부 확인 > 가격 타입 확인 V2
 * @description B2B 가격 타입인 경우 골드 클래스 타입.
 * {@link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29233997/B2B+API}
 * @param priceType
 */
export const getGlobalVipBadgeTypeV2 = (priceType: TAriProxyApiRatePlanPriceType) => {
  switch (priceType) {
    case AriProxyApiRatePlanPriceType.B2B:
      return CommonVipBadgeType.GOLD
    default:
      return undefined
  }
}

/**
 * @summary 국내주문 VIP 요금 여부 확인 V2
 */
export const getDomesticVipBadgeTypeV2 = (data?: string): TCommonVipBadgeTypeV2 | '' => {
  const memberClass = getVipMemberClass(data)
  switch (memberClass) {
    case 'LEVEL_01':
      return CommonVipBadgeType.GOLD
    default:
      return ''
  }
}
